import { io, Socket } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

let socket: Socket | null = null;
let isAuthenticated = false;

export const getSocket = (): Socket => {
  if (!socket) {
    socket = io(SOCKET_URL, {
      autoConnect: false,
      withCredentials: true,
    });
  }
  return socket;
};

export const connectSocket = (token: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const sock = getSocket();

    // If already connected, just re-authenticate
    if (sock.connected) {
      authenticateSocket(sock, token)
        .then(resolve)
        .catch(reject);
      return;
    }

    sock.connect();

    sock.once('connect', () => {
      console.log('Socket connected:', sock.id);
      authenticateSocket(sock, token)
        .then(resolve)
        .catch(reject);
    });

    sock.once('connect_error', (error) => {
      console.error('Socket connection error:', error);
      reject(error);
    });

    // Set timeout
    setTimeout(() => {
      if (!sock.connected) {
        reject(new Error('Connection timeout'));
      }
    }, 10000);
  });
};

const authenticateSocket = (sock: Socket, token: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    // Authenticate with the server
    sock.emit('authenticate', token);

    const authTimeout = setTimeout(() => {
      reject(new Error('Authentication timeout'));
    }, 5000);

    sock.once('authenticated', (data: { success: boolean; error?: string }) => {
      clearTimeout(authTimeout);

      if (data.success) {
        console.log('Socket authenticated');
        isAuthenticated = true;
        resolve(true);
      } else {
        console.error('Socket authentication failed:', data.error);
        isAuthenticated = false;

        // Don't disconnect on auth failure - allow retry
        reject(new Error(data.error || 'Authentication failed'));
      }
    });
  });
};

export const disconnectSocket = () => {
  if (socket && socket.connected) {
    socket.disconnect();
    isAuthenticated = false;
    console.log('Socket disconnected');
  }
};

export const reauthenticateSocket = async (token: string): Promise<boolean> => {
  const sock = getSocket();

  if (!sock.connected) {
    // If not connected, do a full connect
    return connectSocket(token);
  }

  // Re-authenticate with new token
  return authenticateSocket(sock, token);
};

export const isSocketAuthenticated = (): boolean => {
  return isAuthenticated;
};

export { socket };
